/**
 * The semen analysis, to and from the spermiogram row the API holds for it.
 *
 * Extracted from `usePhases` so the contract is testable: that module reaches
 * the API client and cannot be imported by a browserless test — the same reason
 * `phaseProcedureDate` and `pgtResult` live beside it.
 *
 * The sperm-freezing `analysis` step used to persist only as a JSON blob on
 * `phase.notes`, a shape this app could read back and nothing else could.
 * `spermiograms` has a typed column for every figure the step collects, and
 * that is what a clinic reads and what `/cycles/:id/spermiograms` returns, so
 * the figures are written there. The blob is still written beside them (see
 * `CycleDetailScreen.handleUpdateStepProgress`) so a value with no column of
 * its own survives, and `analysisDataFromSpermiogram` reads the columns back in
 * preference to it.
 */

import type { ResponseBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
import type { PhaseSubObjects } from "./phase-request-mappers";
import {
  spermiogramRowForDisplay,
  spermiogramRowOwnedByPatient,
} from "./spermiogramRows";

type ApiPhase = ResponseBody<
  paths["/{tenantId}/cycles/{id}/phases"]["get"]
>["data"][number];

type ApiSpermiogram = NonNullable<ApiPhase["spermiograms"]>[number];

/**
 * The columns the read-back looks at, and nothing more — so a caller (a test,
 * or a future surface holding one figure) is not made to supply forty fields it
 * has no opinion about. A real API row satisfies this.
 */
export type AnalysisSpermiogramRead = Partial<
  Pick<
    ApiSpermiogram,
    | "id"
    | "source"
    | "spermConcentration"
    | "spermCount"
    | "motility"
    | "progressiveMotility"
    | "normalMorphology"
    | "semenVolume"
    | "volumeUnit"
    | "notes"
  >
>;
export type SpermiogramWrite = NonNullable<
  PhaseSubObjects["spermiograms"]
>[number];

type StepData = Record<string, string | number | boolean | null>;

/** Step field → spermiogram column, for the figures the analysis collects. */
const ANALYSIS_FIELD_TO_COLUMN = {
  concentration: "spermConcentration",
  totalCount: "spermCount",
  motilityPercent: "motility",
  progressiveMotilityPercent: "progressiveMotility",
  morphologyPercent: "normalMorphology",
  volumeMl: "semenVolume",
} as const satisfies Record<
  string,
  keyof ApiSpermiogram & keyof SpermiogramWrite
>;

/**
 * The row the analysis step displays and the row its save may write: the
 * shared spermiogram selectors, kept under their step-specific names so the
 * call sites and tests read as they did when the rule was first stated here.
 */
export function analysisRowForDisplay<T extends AnalysisSpermiogramRead>(
  rows: readonly T[] | undefined | null,
): T | undefined {
  return spermiogramRowForDisplay(rows);
}

export function analysisRowOwnedByPatient<T extends AnalysisSpermiogramRead>(
  rows: readonly T[] | undefined | null,
): T | undefined {
  return spermiogramRowOwnedByPatient(rows);
}

/**
 * A figure she entered, as the API wants it.
 *
 * Three states, not two. `undefined` leaves the key off the write entirely;
 * `null` reaches the column as a null, which is what makes a correction that
 * empties a field possible at all — dropping it would make clearing a value
 * impossible.
 *
 * Be precise about what the `undefined` arm buys, because it is easy to read
 * more into it. `StepDetailsModal` re-seeds its buffer from the saved step data
 * every time it opens, and that data now carries every non-null column, so a
 * save through the dialogue sends all six figures whether she edited them or
 * not. What that guarantees is only that a value is never *blanked* by a save
 * that did not touch it — the figure is rewritten with the same number. It does
 * NOT guarantee that an untouched column is left alone: if the clinic changes a
 * value between the dialogue opening and her pressing save, her buffered copy
 * overwrites the newer one, and nothing here detects the conflict. The
 * `undefined` arm is therefore a guard for a caller that sends a sparse object,
 * not a description of what the current dialogue does.
 */
export function analysisNumber(
  data: StepData,
  field: string,
): number | null | undefined {
  if (!Object.prototype.hasOwnProperty.call(data, field)) return undefined;
  const value = data[field];
  if (value === null || value === "" || typeof value === "boolean") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * The spermiogram write for an analysis step save, or null when she entered
 * nothing — an empty row would tell the clinic an analysis exists with every
 * figure missing.
 */
export function buildAnalysisSpermiogram(
  data: StepData,
  patientId: string,
  existingSpermiogramId?: string | null,
): SpermiogramWrite | null {
  // `source` is stated on every write, not left to a default. These figures are
  // hers — read off a report and typed in — and a record that cannot say so is
  // a record a clinician may read as the laboratory's own. The id, when there
  // is one, only ever comes from `analysisRowOwnedByPatient`.
  const sperm: SpermiogramWrite = { patientId, source: "patient" };
  if (existingSpermiogramId) sperm.id = existingSpermiogramId;

  let touched = false;
  for (const [field, column] of Object.entries(ANALYSIS_FIELD_TO_COLUMN)) {
    const value = analysisNumber(data, field);
    if (value === undefined) continue;
    sperm[column] = value;
    touched = true;
    if (column === "semenVolume") {
      // The unit travels with the number instead of being stamped `mL`.
      // Hydration hands her buffer whatever the displayed row measured, so a
      // clinic volume recorded in another unit was being copied into her row
      // relabelled as millilitres — the figure unchanged and its meaning
      // rewritten. A volume she typed herself carries no unit in the data and
      // the field asks for millilitres, which is where the fallback applies.
      const carried =
        typeof data.volumeUnit === "string" && data.volumeUnit.trim()
          ? data.volumeUnit
          : "mL";
      sperm.volumeUnit = value === null ? null : carried;
    }
  }
  if (Object.prototype.hasOwnProperty.call(data, "notes")) {
    sperm.notes =
      typeof data.notes === "string" && data.notes.trim() ? data.notes : null;
    touched = true;
  }

  return touched ? sperm : null;
}

/**
 * The analysis step's fields, read back off the spermiogram columns.
 *
 * Two regimes, decided by who wrote the row.
 *
 * A row of hers reports only the columns that hold something, because a row
 * saved before the step wrote these columns keeps its figures in the
 * `phase.notes` blob: overlaying nulls from the columns would erase what she had
 * already recorded, so the blob supplies whatever they do not and her next save
 * moves it in.
 *
 * The clinic's row reports its nulls too. Skipping them let the blob refill a
 * figure the clinic had deliberately *cleared* — the old number reappeared as
 * though it still stood, and her next save wrote it back into the column. A
 * laboratory record is authoritative about absence as much as about value, so
 * for a provider row every field is returned, null included.
 */
export function analysisDataFromSpermiogram(
  sperm: AnalysisSpermiogramRead | undefined | null,
): StepData {
  if (!sperm) return {};
  const clinicOwned = sperm.source === "provider";
  const data: StepData = {};
  for (const [field, column] of Object.entries(ANALYSIS_FIELD_TO_COLUMN)) {
    const value = sperm[column];
    if (value != null) data[field] = value;
    else if (clinicOwned) data[field] = null;
  }
  if (sperm.notes) data.notes = sperm.notes;
  else if (clinicOwned) data.notes = null;
  // The unit the row actually carries. It was written and never read, so a
  // clinic row stored in anything but millilitres read as millilitres. The
  // volume field now shows this unit as its choice.
  if (sperm.volumeUnit) data.volumeUnit = sperm.volumeUnit;
  return data;
}
